import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";

export default function Pricing() {
  const { user, refreshBilling } = useAuth();
  const nav = useNavigate();
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [busy, setBusy] = useState(false);

  const onStartTrial = async () => {
    if (!user) {
      nav("/auth?mode=signup");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/billing/checkout-session", {
        plan: billingCycle,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <>
      <Header />
      <div className="lq-container page-enter" data-testid="pricing-page">
        <div style={{ textAlign: "center", maxWidth: 600, margin: "0 auto" }}>
          <div className="eyebrow">— Pricing</div>
          <h1 className="serif" style={{ fontSize: 56, lineHeight: 1.05, margin: "16px 0 14px" }}>
            Simple. Honest. Pay when you grow.
          </h1>
          <p style={{ color: "var(--text-mute)", fontSize: 15, lineHeight: 1.6 }}>
            Track up to 25 songs free, forever. Upgrade to Pro for unlimited tracks and the auto-features that save you hours per release.
          </p>

          {/* Billing toggle */}
          <div
            style={{
              display: "inline-flex",
              padding: 4,
              border: "1px solid var(--border-soft)",
              borderRadius: 999,
              background: "rgba(245,240,232,0.04)",
              marginTop: 28,
              gap: 4,
            }}
          >
            <ToggleBtn active={billingCycle === "monthly"} onClick={() => setBillingCycle("monthly")} data-testid="toggle-monthly">
              Monthly
            </ToggleBtn>
            <ToggleBtn active={billingCycle === "annual"} onClick={() => setBillingCycle("annual")} data-testid="toggle-annual">
              Annual <span style={{ marginLeft: 6, color: "var(--green-light)", fontSize: 9 }}>25% OFF</span>
            </ToggleBtn>
          </div>
        </div>

        {/* Plans */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 18, marginTop: 48 }}>
          <div className="plan" data-testid="free-plan">
            <div className="plan-name">Free</div>
            <div className="plan-price-row">
              <span className="plan-currency">$</span>
              <span className="plan-price">0</span>
              <span className="plan-period">forever</span>
            </div>
            <div className="plan-tag">No credit card · Cancel any time</div>
            <ul className="plan-features">
              <li>Up to <strong>25 tracks</strong></li>
              <li>Manual track entry &amp; editing</li>
              <li>Search, filter, status pipeline</li>
              <li>Manual registry lookup links</li>
              <li>Cloud sync across devices</li>
              <li>JSON &amp; CSV export</li>
              <li className="muted">Streaming-link auto-import</li>
              <li className="muted">Bulk artist import (Apple Music)</li>
              <li className="muted">Auto cross-reference verification</li>
              <li className="muted">Registration completeness tracking</li>
            </ul>
            <Link to={user ? "/dashboard" : "/auth?mode=signup"} className="btn btn-light btn-block" data-testid="free-plan-cta">
              {user ? "Open Library →" : "Start Free →"}
            </Link>
          </div>

          <div className="plan featured" data-testid="pro-plan">
            <div className="plan-name">Pro</div>
            <div className="plan-price-row">
              <span className="plan-currency">$</span>
              <span className="plan-price">{billingCycle === "monthly" ? "8" : "72"}</span>
              <span className="plan-period">{billingCycle === "monthly" ? "/ month" : "/ year"}</span>
            </div>
            <div className="plan-tag" style={{ color: "var(--green-light)" }}>
              ● 14-day free trial · No credit card required
            </div>
            <ul className="plan-features">
              <li>Everything in Free, plus:</li>
              <li><strong>Unlimited tracks</strong></li>
              <li>Streaming-link auto-import (single track)</li>
              <li>Bulk artist import — Apple Music</li>
              <li>Auto cross-reference (iTunes)</li>
              <li>Registration completeness scorecard</li>
              <li>Re-sync connected services</li>
              <li>Priority support</li>
            </ul>
            <button className="btn btn-primary btn-block" onClick={onStartTrial} disabled={busy} data-testid="pro-plan-cta">
              {busy ? "Loading…" : "Start 14-Day Free Trial →"}
            </button>
            <div style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--text-fade)", letterSpacing: "0.10em", textAlign: "center", marginTop: 12 }}>
              Stripe-secured · Cancel any time
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div style={{ marginTop: 64, paddingTop: 40, borderTop: "1px solid var(--border-soft)", maxWidth: 720, marginInline: "auto" }}>
          <h3 className="serif" style={{ fontSize: 32, marginBottom: 18 }}>Common Questions</h3>
          {[
            ["Do I need a credit card to start?", "No. Both Free and the 14-day Pro trial start with just an email and password. We collect a card only if you decide to keep Pro after the trial."],
            ["What happens to my tracks if I downgrade?", "Your data is always yours. If you go from Pro to Free with more than 25 tracks, you keep full read access and can edit/export any of them. New tracks are gated until you upgrade or remove some."],
            ["Can I cancel anytime?", "Yes — one click in Settings. You'll keep Pro features until the end of your current billing period."],
            ["Is my data private?", "Yes. Your catalog is private to your account, never shared, never sold, never used for training. You can export everything as JSON anytime."],
          ].map(([q, a]) => (
            <div key={q} style={{ padding: "16px 0", borderBottom: "1px solid var(--border-soft)" }}>
              <div className="mono" style={{ color: "var(--text)", fontSize: 12, letterSpacing: "0.10em", marginBottom: 6, textTransform: "uppercase" }}>{q}</div>
              <div style={{ color: "var(--text-mute)", fontSize: 14, lineHeight: 1.6 }}>{a}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function ToggleBtn({ active, children, ...rest }) {
  return (
    <button
      style={{
        padding: "8px 16px",
        background: active ? "var(--bg-elev-2)" : "transparent",
        color: active ? "var(--text)" : "var(--text-mute)",
        border: 0, borderRadius: 999, cursor: "pointer",
        fontFamily: "var(--mono)", fontSize: 11,
        letterSpacing: "0.16em", textTransform: "uppercase",
        transition: "all 0.15s ease",
      }}
      {...rest}
    >
      {children}
    </button>
  );
}
